/**
 * Offline Sync Service
 * Guarda operaciones pendientes en IndexedDB cuando no hay conexión
 * y las sincroniza automáticamente cuando vuelve internet.
 */

import { getToken } from './tokenStore';

const DB_NAME = 'maldolor-offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_operations';

export interface PendingOperation {
  id?: number;
  type: 'create' | 'update' | 'delete';
  entity: 'headache' | 'tension' | 'lloro';
  data: any;
  entityId?: number;
  timestamp: number;
  synced: boolean;
}

type SyncListener = (pending: number) => void;

class OfflineSyncService {
  private db: IDBDatabase | null = null;
  private listeners: SyncListener[] = [];
  private syncing = false;

  constructor() {
    this.initDB();
    this.setupConnectivityListeners();
  }

  // Inicializar IndexedDB
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
          store.createIndex('synced', 'synced', { unique: false });
          store.createIndex('entity', 'entity', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Listeners para cambios de conectividad
  private setupConnectivityListeners() {
    window.addEventListener('online', () => {
      console.log('[OfflineSync] Conexion recuperada, sincronizando...');
      this.syncPending();
    });
  }

  // Guardar una operacion pendiente
  async saveOperation(op: Omit<PendingOperation, 'id' | 'timestamp' | 'synced'>): Promise<void> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.add({
        ...op,
        timestamp: Date.now(),
        synced: false,
      });
      tx.oncomplete = () => {
        this.notifyListeners();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  // Obtener operaciones pendientes (no sincronizadas)
  async getPending(): Promise<PendingOperation[]> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => {
        const results = (request.result as PendingOperation[]).filter(op => !op.synced);
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Contar operaciones pendientes
  async getPendingCount(): Promise<number> {
    const pending = await this.getPending();
    return pending.length;
  }

  // Marcar operacion como sincronizada
  private async markSynced(id: number): Promise<void> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(id);
      request.onsuccess = () => {
        const op = request.result;
        if (op) {
          op.synced = true;
          store.put(op);
        }
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Eliminar operaciones ya sincronizadas
  private async cleanSynced(): Promise<void> {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => {
        const all = request.result as PendingOperation[];
        all.filter(op => op.synced).forEach(op => store.delete(op.id!));
        resolve();
      };
      tx.oncomplete = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Sincronizar todas las operaciones pendientes
  async syncPending(): Promise<{ synced: number; failed: number }> {
    if (this.syncing || !navigator.onLine) return { synced: 0, failed: 0 };

    this.syncing = true;
    let synced = 0;
    let failed = 0;

    try {
      const pending = await this.getPending();
      if (pending.length === 0) return { synced: 0, failed: 0 };

      console.log(`[OfflineSync] Sincronizando ${pending.length} operaciones pendientes...`);

      const token = getToken();
      if (!token) {
        console.warn('[OfflineSync] No hay token, no se puede sincronizar');
        return { synced: 0, failed: pending.length };
      }

      const API_BASE = '/api';

      for (const op of pending) {
        try {
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          };

          let url = '';
          let method = '';
          let body: string | undefined;

          // Construir la request segun tipo de operacion
          const entityPath = op.entity === 'headache' ? 'headaches' : op.entity === 'tension' ? 'tensions' : 'lloros';

          switch (op.type) {
            case 'create':
              url = `${API_BASE}/${entityPath}`;
              method = 'POST';
              body = JSON.stringify(op.data);
              break;
            case 'update':
              url = `${API_BASE}/${entityPath}/${op.entityId}`;
              method = 'PUT';
              body = JSON.stringify(op.data);
              break;
            case 'delete':
              url = `${API_BASE}/${entityPath}/${op.entityId}`;
              method = 'DELETE';
              break;
          }

          const response = await fetch(url, { method, headers, body });

          if (response.ok) {
            await this.markSynced(op.id!);
            synced++;
            console.log(`[OfflineSync] Sincronizado: ${op.type} ${op.entity} ${op.entityId || ''}`);
          } else {
            failed++;
            console.error(`[OfflineSync] Error ${response.status}: ${op.type} ${op.entity}`);
          }
        } catch (err) {
          failed++;
          console.error(`[OfflineSync] Error sincronizando op ${op.id}:`, err);
        }
      }

      await this.cleanSynced();
      this.notifyListeners();

      console.log(`[OfflineSync] Completado: ${synced} sincronizados, ${failed} fallidos`);
    } finally {
      this.syncing = false;
    }

    return { synced, failed };
  }

  // Suscribirse a cambios en operaciones pendientes
  onPendingChange(listener: SyncListener): () => void {
    this.listeners.push(listener);
    // Notificar estado actual
    this.getPendingCount().then(count => listener(count));
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private async notifyListeners() {
    const count = await this.getPendingCount();
    this.listeners.forEach(l => l(count));
  }

  // Comprobar si hay conexion
  isOnline(): boolean {
    return navigator.onLine;
  }
}

// Singleton
export const offlineSync = new OfflineSyncService();
